import React, { useEffect, useState, memo, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSessionLogs } from "../redux/actions/index";
import OBCSpinner from "./util/OBC";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faMap } from "@fortawesome/pro-solid-svg-icons";
import { Select, Tooltip } from "antd";
import _ from "lodash";
import { convertToTimezone, convertTsToMilliseconds } from "./util/TimezoneUtils";

const sessionIDSelector = (state) => state.playlist.data.routeID;
const sessionLogsSelector = (state) => state.playlist.logData;
const convertToUtcSelector = (state) => state.userDetails.userConfig.convert_to_utc;

const sourceIconDict = {
    mapmatcher: <FontAwesomeIcon icon={faMap} />,
};

const copyClicked = (link) => {
    if (navigator.clipboard) {
        navigator.clipboard.writeText(link);
    }
};

const LogListItem = ({ logObject }) => {
    const convertToUtc = useSelector(convertToUtcSelector);

    const humanDate = useMemo(() => {
        const dateTime = new Date(convertTsToMilliseconds(logObject.timestamp));
        return convertToTimezone(dateTime, convertToUtc, {
            year: "numeric",
            month: "short",
            weekday: "short",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "2-digit",
        });
    }, [convertToUtc, logObject.timestamp]);

    const { subsystem, log } = logObject;
    const isString = typeof log === "string";

    const _logObject = isString ? {} : logObject.log;
    if (isString) {
        const keyValuePairs = log.split(";");
        keyValuePairs.forEach((kvp) => {
            const thisLog = kvp.replace(/\s/g, ""); // Remove whitespaces
            if (thisLog.indexOf("<https://") === 0) {
                const splitUrl = thisLog.split("|"); // Split out label
                const tidyLabel = splitUrl[1].replace(">", ":");
                const tidyUrl = splitUrl[0].replace("<", "");
                const link = (
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={tidyUrl}>
                        {tidyUrl}
                    </a>
                );
                _logObject[tidyLabel] = link;
            } else {
                const splitLog = thisLog.split(":");
                _logObject[splitLog[0]] = splitLog[1];
            }
        });
    } else {
        Object.assign(_logObject, logObject.log);
    }

    return (
        <div className={isString ? "LogRow" : "LogRowObj"}>
            <div className={"LogRowObj__Header"}>
                <span>{humanDate}</span>
                <Tooltip title={subsystem}>
                    <span>{sourceIconDict[subsystem] ?? subsystem}</span>
                </Tooltip>
            </div>
            {Object.keys(_logObject).map((key) =>
                isString ? (
                    <div
                        className="LogRow__Item"
                        key={key}>
                        <p className="label">{key}</p>
                        <p>{_logObject[key]}</p>
                        {_.isObject(_logObject[key]) && _logObject[key].type === "a" && (
                            <button
                                className="LogRow__Item__Copy"
                                onClick={() => copyClicked(_logObject[key].props.href)}>
                                <FontAwesomeIcon icon={faCopy} />
                            </button>
                        )}
                    </div>
                ) : (
                    <ul
                        className="LogRowObj__List"
                        key={key}>
                        <li>
                            <strong>{key} - </strong>
                            {!_.isObject(_logObject[key]) && <span style={{ userSelect: "text" }}>{_logObject[key]}</span>}
                        </li>
                    </ul>
                ),
            )}
        </div>
    );
};

const SessionLogs = memo(() => {
    const dispatch = useDispatch();
    const sessionID = useSelector(sessionIDSelector);
    const logData = useSelector(sessionLogsSelector);
    const [loading, setLoading] = useState(true);
    const [orderOption, setOrderOption] = useState("desc");

    const { Option } = Select;

    const hasLogs = !loading && logData.length > 0;

    useEffect(() => {
        if (sessionID) {
            dispatch(
                getSessionLogs(sessionID, () => {
                    setLoading(false);
                }),
            );
        }
    }, [sessionID, dispatch]);

    const sortLogData = useMemo(() => {
        return _.orderBy(logData, ["timestamp"], [orderOption]);
    }, [orderOption, logData]);

    if (loading) {
        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    height: "100%",
                }}>
                <OBCSpinner colorScheme={"mono"} />
            </div>
        );
    }

    return (
        <div className="LogRowContainer">
            <div className="LogRowOrderBy">
                <div className="LogRowLabel">Sort by:</div>
                <Select
                    value={orderOption}
                    onChange={(e) => setOrderOption(e)}
                    className="LogRowSelect">
                    <Option value="desc">Newest first</Option>
                    <Option value="asc">Oldest first</Option>
                </Select>
            </div>
            {hasLogs ? sortLogData.map((logObject) => <LogListItem logObject={logObject} />) : <span className="Text">No Session Logs Found</span>}
        </div>
    );
});

export default SessionLogs;
